import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Coupon, Product, PaymentInterval } from 'src/types/payment';
import { submitCoupon } from 'src/requests/registration';

export interface CartItem {
  product: Product;
  interval?: PaymentInterval;
  priceId: string;
  price: number;
  currency: string;
  coreSubscriptionId?: string;
  addedAt: string;
  quantity: number;
}

export interface CartState {
  items: CartItem[];
  isOpen: boolean;
  coupon: Coupon | null;
  appliedDiscount: number;
  totalPrice: number;
  totalItems: number;
  isApplyingCoupon: boolean;
  expiresAt: string;
}

export const restoreCart = (): CartState | null => {
  let cart = null;

  try {
    const storedData: string | null = window.localStorage.getItem('cart');

    if (storedData) {
      cart = JSON.parse(storedData);

      if (new Date() > new Date(cart.expiresAt)) {
        window.localStorage.removeItem('cart');
        return null;
      }
    }
  } catch (err) {
    console.error(err);
    window.localStorage.removeItem('cart');
  }

  return cart;
};

export const storeCart = (cart: CartState): void => {
  const expiry = new Date();
  expiry.setHours(expiry.getHours() + 48);
  cart.expiresAt = expiry.toISOString();

  window.localStorage.setItem('cart', JSON.stringify(cart));
};

const initialState: CartState = {
  items: [],
  isOpen: false,
  coupon: null,
  appliedDiscount: 0,
  totalPrice: 0,
  totalItems: 0,
  isApplyingCoupon: false,
  expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
};

const calculateTotals = (items: CartItem[], coupon: Coupon | null) => {
  let subtotal = 0;
  let totalItems = 0;

  items.forEach((item) => {
    subtotal += item.price * item.quantity;
    totalItems += item.quantity;
  });

  let appliedDiscount = 0;
  if (coupon) {
    const eligibleItems = items.filter(
      (item) => coupon.productIDs.length === 0 || coupon.productIDs.includes(item.product.productId)
    );

    const eligibleSubtotal = eligibleItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    if (coupon.type === 'percentOff') {
      appliedDiscount = (eligibleSubtotal * coupon.value) / 100;
    } else if (coupon.type === 'amountOff') {
      appliedDiscount = Math.min(coupon.value, eligibleSubtotal);
    }
  }

  return {
    totalPrice: Math.max(0, subtotal - appliedDiscount),
    totalItems,
    appliedDiscount,
  };
};

const slice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    hydrateCart(state: CartState, action: PayloadAction<CartState>) {
      return { ...action.payload, isOpen: false };
    },
    addToCart(
      state: CartState,
      action: PayloadAction<{
        product: Product;
        interval?: PaymentInterval;
        coreSubscriptionId?: string;
      }>
    ) {
      const { product, interval, coreSubscriptionId } = action.payload;

      let price: any;
      let priceId: string;
      let currency: string;
      let priceAmount: number;

      if (product.prices && product.prices.length > 0) {
        price = interval
          ? product.prices.find((p) => p.interval === interval) || product.prices[0]
          : product.prices[0];
      } else if (product.price) {
        price = product.price;
      } else {
        return;
      }

      priceId = price.priceId;
      currency = price.currency;
      priceAmount = price.unitAmount;

      const existingItemIndex = state.items.findIndex(
        (item) => item.product.productId === product.productId && item.interval === interval
      );

      if (existingItemIndex >= 0) {
        state.items[existingItemIndex].quantity += 1;
      } else {
        const newItem: CartItem = {
          product,
          interval,
          priceId,
          price: priceAmount,
          currency,
          coreSubscriptionId,
          addedAt: new Date().toISOString(),
          quantity: 1,
        };
        state.items.push(newItem);
      }

      const totals = calculateTotals(state.items, state.coupon);
      state.totalPrice = totals.totalPrice;
      state.totalItems = totals.totalItems;
      state.appliedDiscount = totals.appliedDiscount;

      storeCart(state);
    },

    removeFromCart(
      state: CartState,
      action: PayloadAction<{
        productId: string;
        interval?: PaymentInterval;
      }>
    ) {
      const { productId, interval } = action.payload;
      state.items = state.items.filter(
        (item) => !(item.product.productId === productId && item.interval === interval)
      );

      const totals = calculateTotals(state.items, state.coupon);
      state.totalPrice = totals.totalPrice;
      state.totalItems = totals.totalItems;
      state.appliedDiscount = totals.appliedDiscount;

      storeCart(state);
    },

    updateItemQuantity(
      state: CartState,
      action: PayloadAction<{
        productId: string;
        interval?: PaymentInterval;
        quantity: number;
      }>
    ) {
      const { productId, interval, quantity } = action.payload;
      const itemIndex = state.items.findIndex(
        (item) => item.product.productId === productId && item.interval === interval
      );

      if (itemIndex >= 0) {
        if (quantity <= 0) {
          state.items.splice(itemIndex, 1);
        } else {
          state.items[itemIndex].quantity = quantity;
        }

        const totals = calculateTotals(state.items, state.coupon);
        state.totalPrice = totals.totalPrice;
        state.totalItems = totals.totalItems;
        state.appliedDiscount = totals.appliedDiscount;

        storeCart(state);
      }
    },

    clearCart(state: CartState) {
      state.items = [];
      state.totalPrice = 0;
      state.totalItems = 0;
      state.appliedDiscount = 0;

      try {
        window.localStorage.removeItem('cart');
      } catch (err) {
        console.error(err);
      }
    },

    toggleCart(state: CartState, action: PayloadAction<boolean | undefined>) {
      state.isOpen = action.payload !== undefined ? action.payload : !state.isOpen;
    },

    applyCouponStart(state: CartState) {
      state.isApplyingCoupon = true;
    },

    applyCouponEnd(state: CartState) {
      state.isApplyingCoupon = false;
    },

    applyCouponSuccess(state: CartState, action: PayloadAction<Coupon>) {
      state.coupon = action.payload;
      state.isApplyingCoupon = false;

      const totals = calculateTotals(state.items, state.coupon);
      state.totalPrice = totals.totalPrice;
      state.appliedDiscount = totals.appliedDiscount;

      storeCart(state);
    },

    removeCoupon(state: CartState) {
      state.coupon = null;

      const totals = calculateTotals(state.items, null);
      state.totalPrice = totals.totalPrice;
      state.appliedDiscount = 0;

      storeCart(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase('LOGOUT', (state) => {
      state.items = [];
      state.totalPrice = 0;
      state.totalItems = 0;
      state.appliedDiscount = 0;
      state.coupon = null;
      state.isOpen = false;

      try {
        window.localStorage.removeItem('cart');
      } catch (err) {
        console.error(err);
      }
    });
  },
});

export const {
  addToCart,
  removeFromCart,
  updateItemQuantity,
  clearCart,
  toggleCart,
  applyCouponStart,
  applyCouponEnd,
  applyCouponSuccess,
  removeCoupon,
  hydrateCart,
} = slice.actions;

export const applyCoupon = async (
  couponCode: string,
  dispatch: any
): Promise<{ success: boolean; message?: string; coupon?: Coupon }> => {
  dispatch(applyCouponStart());

  try {
    const submitCouponResponse = await submitCoupon(couponCode);

    if (
      !submitCouponResponse ||
      !submitCouponResponse.data ||
      !submitCouponResponse.data.valid ||
      !submitCouponResponse.data.appliesTo ||
      !submitCouponResponse.data.appliesTo.products ||
      !submitCouponResponse.data.promoCode
    ) {
      dispatch(applyCouponEnd());
      return { success: false, message: 'Invalid coupon code' };
    }

    const couponType =
      submitCouponResponse.data.amountOff && submitCouponResponse.data.amountOff > 0
        ? 'amountOff'
        : 'percentOff';

    const couponValue =
      submitCouponResponse.data[couponType] && submitCouponResponse.data[couponType] > 0
        ? (submitCouponResponse.data[couponType] as number)
        : null;

    if (!couponValue) {
      dispatch(applyCouponEnd());
      return { success: false, message: 'The coupon code you submitted was invalid.' };
    }

    const validCoupon = {
      type: couponType,
      value: couponValue,
      productIDs: submitCouponResponse.data.appliesTo.products,
      promoCode: submitCouponResponse.data.promoCode,
      isTrial: submitCouponResponse.data.isTrial,
      durationMonths: submitCouponResponse.data.durationMonths,
    } as Coupon;

    dispatch(applyCouponSuccess(validCoupon));
    return {
      success: true,
      message: `Coupon "${couponCode}" applied successfully!`,
      coupon: validCoupon,
    };
  } catch (error) {
    console.error('Coupon validation error:', error);
    dispatch(applyCouponEnd());
    return { success: false, message: 'Failed to apply coupon. Please try again.' };
  }
};

export const { reducer } = slice;
